import { ArrowRight, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';

const HERO_BG = "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/qwka8ob3_abstract-night-city-backdrop-with-digital-connecti-2026-01-11-08-42-55-utc.jpg";

export const HeroSection = () => {
  const { t } = useLanguage();

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSolutions = () => {
    document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen pt-20 overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={HERO_BG} 
          alt="" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/70" />
      </div>
      
      <div className="container-0g relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-[calc(100vh-5rem)] py-12">
          {/* Left Content */}
          <div className="flex flex-col justify-center animate-slide-up">
            {/* Label */}
            <div className="inline-flex items-center space-x-2 mb-6">
              <span className="tech-badge bg-teal-500/20 border-teal-500/30 text-teal-400">{t('hero.badge')}</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.1]">
              {t('hero.title')}{' '}
              <span className="text-teal-400">{t('hero.titleHighlight')}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-xl leading-relaxed">
              {t('hero.subtitle')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="btn-primary inline-flex items-center justify-center gap-2 text-base"
                onClick={scrollToContact}
                data-testid="hero-cta-primary"
              >
                <Calendar className="h-5 w-5" />
                {t('hero.ctaPrimary')}
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 text-base"
                onClick={scrollToSolutions}
                data-testid="hero-cta-secondary"
              >
                {t('hero.ctaSecondary')}
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="mt-12 pt-8 border-t border-white/20">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t('hero.trustedTech')}</p>
              <div className="flex flex-wrap gap-6 text-sm font-medium text-slate-300">
                <span>LoRaWAN</span>
                <span className="text-slate-500">|</span>
                <span>Sigfox</span>
                <span className="text-slate-500">|</span>
                <span>NB-IoT</span>
                <span className="text-slate-500">|</span>
                <span>BLE</span>
                <span className="text-slate-500">|</span>
                <span>Satellite</span>
              </div>
            </div>
          </div>

          {/* Right - Stats */}
          <div className="relative animate-fade-in hidden lg:flex flex-col items-end justify-center space-y-8">
            {/* Floating stat cards */}
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20">
              <p className="text-4xl font-bold text-teal-400">15+</p>
              <p className="text-sm text-slate-300">{t('hero.yearsExp')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20">
              <p className="text-4xl font-bold text-teal-400">100+</p>
              <p className="text-sm text-slate-300">{t('hero.deployedDevices')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20">
              <p className="text-4xl font-bold text-teal-400">3</p>
              <p className="text-sm text-slate-300">{t('hero.balticCountries')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
